import React from "react";
import { Link } from "react-router-dom";

/* =========================================================================
 *  Shared renderer for the Apsession legal pages — Privacy Policy, Terms of
 *  Service, and Organizer Agreement.
 *
 *  Each page supplies only its text (see the `sections` shape below); the
 *  masthead, sticky table of contents, typography, and cross-document linking
 *  live here so the three documents can never drift apart visually.
 *
 *  Block shapes accepted inside a section's `blocks` array:
 *    { p: "..." }                     paragraph
 *    { p: "...", strong: true }       paragraph set off in bold
 *    { list: ["...", ...] }           bulleted list
 *    { olist: ["...", ...] }          numbered list
 *    { defs: [["Term", "meaning"]] }  term — definition pairs
 *    { sub: "2.1 Title", blocks: [] } numbered sub-section
 *    { contact: true }                the Apsession contact card
 * ========================================================================= */

// Mentions of a sibling document become links to it. Longest phrases first so
// "Apsession Privacy Policy" wins over the bare "Privacy Policy" inside it.
const CROSS_LINKS = [
    { phrase: "Apsession Privacy Policy", to: "/privacy-policy" },
    { phrase: "Apsession Terms of Service", to: "/terms-of-service" },
    { phrase: "Apsession Organizer Agreement", to: "/organizer-agreement" },
    { phrase: "Privacy Policy", to: "/privacy-policy" },
    { phrase: "Terms of Service", to: "/terms-of-service" },
    { phrase: "Organizer Agreement", to: "/organizer-agreement" },
];

const escapeRegExp = (s) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/*
 * Turns cross-document mentions in `text` into router links, skipping any
 * pointing at the page doing the rendering — a document that links to itself
 * reads like a mistake, and the Terms open by defining their own name.
 */
const linkify = (text, selfPath) => {
    const targets = CROSS_LINKS.filter((l) => l.to !== selfPath);
    if (!targets.length) return text;

    const pattern = new RegExp(`(${targets.map((t) => escapeRegExp(t.phrase)).join("|")})`, "g");
    const parts = text.split(pattern);
    if (parts.length === 1) return text;

    return parts.map((part, i) => {
        const hit = targets.find((t) => t.phrase === part);
        if (!hit) return part;
        return (
            <Link
                key={i}
                to={hit.to}
                className="font-semibold text-gray-900 underline decoration-[#ffea00] decoration-2 underline-offset-2"
            >
                {part}
            </Link>
        );
    });
};

const Bullet = () => (
    <span className="mt-[9px] mr-3 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#ffea00]" />
);

const ContactBlock = () => (
    <div className="border-l-4 border-[#ffea00] bg-gray-50 px-5 py-4 my-5 rounded-r-md">
        <p className="font-bold text-gray-900 m-0">Apsession Limited</p>
        <p className="text-gray-700 m-0">Jamaica</p>
        <p className="text-gray-700 m-0">
            Email:{" "}
            <a href="mailto:support@apsession.com" className="text-gray-900 font-semibold underline decoration-[#ffea00] decoration-2 underline-offset-2">
                support@apsession.com
            </a>
        </p>
        <p className="text-gray-700 m-0">
            Website:{" "}
            <a href="https://apsession.com" target="_blank" rel="noopener noreferrer" className="text-gray-900 font-semibold underline decoration-[#ffea00] decoration-2 underline-offset-2">
                apsession.com
            </a>
        </p>
    </div>
);

// Sub-sections recurse through their own block list, so this is a component
// rather than inline JSX in the section map.
const Block = ({ block, selfPath }) => {
    if (block.contact) return <ContactBlock />;

    if (block.p) {
        return (
            <p className={`leading-relaxed mb-4 ${block.strong ? "font-bold text-gray-900" : "text-gray-700"}`}>
                {linkify(block.p, selfPath)}
            </p>
        );
    }

    if (block.list) {
        return (
            <ul className="mb-5 space-y-2 list-none" style={{ padding: 0 }}>
                {block.list.map((item) => (
                    <li key={item} className="flex items-start text-gray-700 leading-relaxed">
                        <Bullet />
                        <span>{linkify(item, selfPath)}</span>
                    </li>
                ))}
            </ul>
        );
    }

    if (block.olist) {
        return (
            <ol className="mb-5 space-y-2 list-none" style={{ padding: 0 }}>
                {block.olist.map((item, i) => (
                    <li key={item} className="flex items-start text-gray-700 leading-relaxed">
                        <span className="mr-3 font-bold text-gray-900 flex-shrink-0">{i + 1}.</span>
                        <span>{linkify(item, selfPath)}</span>
                    </li>
                ))}
            </ol>
        );
    }

    if (block.defs) {
        return (
            <dl className="mb-5 space-y-3" style={{ margin: 0 }}>
                {block.defs.map(([term, meaning]) => (
                    <div key={term} className="flex items-start text-gray-700 leading-relaxed">
                        <Bullet />
                        <div>
                            <dt className="inline font-bold text-gray-900">{term}</dt>
                            <dd className="inline m-0"> — {linkify(meaning, selfPath)}</dd>
                        </div>
                    </div>
                ))}
            </dl>
        );
    }

    if (block.sub) {
        return (
            <div className="mt-8 mb-2">
                <h3 className="text-lg font-extrabold text-gray-900 mb-3">{block.sub}</h3>
                {block.blocks.map((b, i) => (
                    <Block key={i} block={b} selfPath={selfPath} />
                ))}
            </div>
        );
    }

    return null;
};

export default function LegalDocument({ title, path, effectiveDate, lastUpdated, intro, sections }) {
    return (
        <main className="bg-white">
            {/* Masthead — mirrors the footer's black/yellow treatment so the
                legal pages read as part of the site, not a bare document. */}
            <div className="bg-[#0a0a0a] text-white" style={{ borderBottom: "8px solid #ffea00" }}>
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 sm:py-20">
                    <p className="text-[#ffea00] font-bold tracking-widest text-xs uppercase mb-3">Legal</p>
                    <h1 className="text-3xl sm:text-5xl font-black tracking-tight mb-6">{title}</h1>
                    <div className="text-gray-400 font-medium text-sm space-y-1">
                        <p className="m-0">Effective Date: {effectiveDate}</p>
                        <p className="m-0">Last Updated: {lastUpdated}</p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
                <div className="lg:grid lg:grid-cols-[280px_minmax(0,1fr)] lg:gap-14">
                    {/* Table of contents — sticky on desktop, hidden on mobile
                        where dozens of anchors would push the document below
                        the fold. Rendered from the same list as the body, so
                        it cannot fall out of sync. */}
                    <nav className="hidden lg:block" aria-label={`${title} contents`}>
                        <div className="sticky top-24 max-h-[calc(100vh-8rem)] overflow-y-auto pr-2">
                            <p className="text-xs font-black uppercase tracking-widest text-gray-900 mb-4">On this page</p>
                            <ul className="space-y-2 list-none" style={{ padding: 0 }}>
                                {sections.map((section) => (
                                    <li key={section.id}>
                                        <a
                                            href={`#${section.id}`}
                                            className="block text-sm font-medium leading-snug"
                                            style={{ color: "#6b7280", transition: "color 0.2s ease" }}
                                            onMouseEnter={(e) => { e.currentTarget.style.color = "#111827"; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.color = "#6b7280"; }}
                                        >
                                            {section.title}
                                        </a>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </nav>

                    <article className="max-w-3xl">
                        {intro.map((text, i) => (
                            <p key={i} className="text-gray-700 leading-relaxed mb-4 text-[15px] sm:text-base">
                                {linkify(text, path)}
                            </p>
                        ))}

                        {sections.map((section) => (
                            <section key={section.id} id={section.id} className="scroll-mt-24 mt-12">
                                <h2 className="text-xl sm:text-2xl font-black text-gray-900 mb-4">{section.title}</h2>
                                {section.blocks.map((block, i) => (
                                    <Block key={i} block={block} selfPath={path} />
                                ))}
                            </section>
                        ))}

                        {/* The other two documents are part of the same
                            agreement set; a reader who lands here from search
                            should not have to go back to the footer. */}
                        <nav className="mt-16 pt-8 border-t border-gray-200" aria-label="Other legal documents">
                            <p className="text-xs font-black uppercase tracking-widest text-gray-900 mb-4">Related documents</p>
                            <ul className="space-y-3 list-none" style={{ padding: 0 }}>
                                {CROSS_LINKS.slice(0, 3)
                                    .filter((l) => l.to !== path)
                                    .map((l) => (
                                        <li key={l.to}>
                                            <Link to={l.to} className="font-semibold text-gray-900 underline decoration-[#ffea00] decoration-2 underline-offset-4">
                                                {l.phrase}
                                            </Link>
                                        </li>
                                    ))}
                            </ul>
                        </nav>
                    </article>
                </div>
            </div>
        </main>
    );
}
